import {
  Card,
  CardHeader,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from "@fluentui/react-components";
import { CheckmarkCircle24Regular } from "@fluentui/react-icons";
import { pages } from "@microsoft/teams-js";
import { useEffect, useState } from "react";

import { initializeTeams } from "@/lib/teamsClient";

/**
 * Channel-install configuration page.
 *
 * Invoked by Teams when the user installs the tab into a channel or group
 * chat. The host expects us to call ``pages.config.setConfig`` with the
 * tab content URL; only then will it enable the Save button.
 *
 * The UI here is just confirmation feedback — there's nothing to configure
 * per-instance yet (no SSR, no per-tab filters to persist in the tab
 * instance itself). Filter state lives in the URL, so defaults are fine.
 */
export function Configure() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const inTeams = await initializeTeams();
        if (!inTeams) {
          if (active) {
            setPhase("error");
            setMessage(
              "This page must be loaded inside Microsoft Teams during " +
                "channel installation.",
            );
          }
          return;
        }

        const baseUrl = `${window.location.origin}/#/`;
        pages.config.registerOnSaveHandler((saveEvent) => {
          void pages.config
            .setConfig({
              entityId: "document-review",
              contentUrl: baseUrl,
              websiteUrl: baseUrl,
              suggestedDisplayName: "Document Review",
            })
            .then(() => saveEvent.notifySuccess())
            .catch((err: unknown) => {
              saveEvent.notifyFailure(
                err instanceof Error ? err.message : "setConfig failed",
              );
            });
        });

        pages.config.setValidityState(true);
        if (active) setPhase("ready");
      } catch (err) {
        if (active) {
          setPhase("error");
          setMessage(err instanceof Error ? err.message : "Unknown error");
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: tokens.colorNeutralBackground2,
      }}
    >
      <Card style={{ maxWidth: 520, width: "100%" }}>
        <CardHeader
          image={
            phase === "ready" ? (
              <CheckmarkCircle24Regular
                style={{ color: tokens.colorPaletteGreenForeground1 }}
              />
            ) : undefined
          }
          header={
            <Text weight="semibold" size={500}>
              Document Review
            </Text>
          }
          description={
            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
              Review and approve documents extracted from shipment emails
              directly inside this channel.
            </Text>
          }
        />
        <div style={{ padding: "12px 0" }}>
          {phase === "loading" ? (
            <Spinner label="Preparing configuration…" />
          ) : phase === "ready" ? (
            <MessageBar intent="success">
              <MessageBarBody>
                Ready to install. Click <b>Save</b> in the Teams dialog to
                complete setup.
              </MessageBarBody>
            </MessageBar>
          ) : (
            <MessageBar intent="error">
              <MessageBarBody>
                {message ?? "Could not initialize configuration."}
              </MessageBarBody>
            </MessageBar>
          )}
        </div>
      </Card>
    </div>
  );
}
